import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Zap, Target, Award } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

function ElitePage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {/* Back Button */}
      <div className="bg-white border-b border-gray-200 pt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-900 transition" data-testid="elite-back-home-link">
            <ArrowLeft className="w-5 h-5 mr-2" />
            <span className="font-semibold">Back to Home</span>
          </Link>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-2 bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-full text-sm font-semibold mb-4">
            Ages 16-18 • Elite Division
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            High-Intensity Elite Training
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Designed for committed players who want to sharpen every part of their game.
          </p>
        </div>

        {/* Main Image */}
        <div className="mb-12 rounded-2xl overflow-hidden border border-gray-200 shadow-lg">
          <img
            src="https://images.unsplash.com/photo-1527950285759-4d9f1b69c461?w=1200&q=80"
            alt="Elite Division Training"
            className="w-full h-96 object-cover"
          />
        </div>

        {/* What to Expect */}
        <section className="bg-white rounded-2xl border border-gray-200 p-8 mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">What to Expect</h2>
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            The Elite Division is designed for committed players who want to sharpen every part of their game. Expect high reps, advanced technical work, position-specific coaching, and competitive training games.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
              <Zap className="w-10 h-10 text-green-700 mb-3" />
              <h3 className="font-bold text-slate-900 mb-2">High Reps</h3>
              <p className="text-slate-600 text-sm">More touches, faster learning.</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
              <Target className="w-10 h-10 text-blue-700 mb-3" />
              <h3 className="font-bold text-slate-900 mb-2">Game Realism</h3>
              <p className="text-slate-600 text-sm">Decisions under pressure.</p>
            </div>
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-6 border border-slate-200">
              <Award className="w-10 h-10 text-slate-700 mb-3" />
              <h3 className="font-bold text-slate-900 mb-2">Coach Feedback</h3>
              <p className="text-slate-600 text-sm">Clear cues + corrections.</p>
            </div>
          </div>
        </section>

        {/* Training Focus */}
        <section className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-2xl p-8 mb-8 text-white">
          <h2 className="text-2xl font-bold mb-6">Training Focus Areas</h2>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-green-400 mr-3 mt-1 text-xl">✓</span>
              <span className="text-lg">Ball mastery, agility, and 1v1 situations</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-400 mr-3 mt-1 text-xl">✓</span>
              <span className="text-lg">Position-specific training and tactical awareness</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-400 mr-3 mt-1 text-xl">✓</span>
              <span className="text-lg">Finishing, vision, and decision-making under pressure</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-400 mr-3 mt-1 text-xl">✓</span>
              <span className="text-lg">Fast-paced sessions built for serious development</span>
            </li>
          </ul>
        </section>

        {/* Training Highlights Section */}
        <section className="mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-slate-900 mb-4">
            Training Highlights
          </h2>
          <p className="text-center text-slate-600 max-w-2xl mx-auto mb-10">
            Short clips from real sessions — showing coaching quality, structure, and intensity.
          </p>

          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Training Video 1 */}
            <div className="rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden">
              <div className="relative aspect-[9/16] bg-black">
                <video
                  className="absolute inset-0 w-full h-full object-cover"
                  muted
                  autoPlay
                  loop
                  playsInline
                  preload="metadata"
                >
                  <source src="/images/videos/training-1.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/40 via-black/0 to-black/10"></div>
              </div>
            </div>

            {/* Training Video 2 */}
            <div className="rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden">
              <div className="relative aspect-[9/16] bg-black">
                <video
                  className="absolute inset-0 w-full h-full object-cover"
                  muted
                  autoPlay
                  loop
                  playsInline
                  preload="metadata"
                >
                  <source src="/images/videos/training-2.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/40 via-black/0 to-black/10"></div>
              </div>
            </div>

            {/* Training Video 3 */}
            <div className="rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden">
              <div className="relative aspect-[9/16] bg-black">
                <video
                  className="absolute inset-0 w-full h-full object-cover"
                  muted
                  autoPlay
                  loop
                  playsInline
                  preload="metadata"
                >
                  <source src="/images/videos/training-3.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/40 via-black/0 to-black/10"></div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default ElitePage;
